import React, { FC, useCallback, useContext, useMemo } from 'react';
import clsx from 'clsx';
import { Icon } from '@marriott/mi-ui-library';
import { useUserDetailsGlobalStore } from '@marriott/mi-store-utils';

import {
  getMemberLevelCode,
  getMemberLevelPropertiesByCode,
  getMemberLevelV2,
  MEMBERSHIP_LIFETIME_CODES_MAPPING,
} from '../../modules';
import { getCustomClickTrackValues, mapTrackingProperties } from '../../modules/utils/trackingHelper';
import { PageContext } from '../../modules/context';
import { useAccountPersistentStore } from '../../modules/store/accountPersistentStore';
import { useStore } from '../../modules/store/memberLevelStore';
import { StyledPointsMiniBanner } from './pointsMiniBanner.styles';
import { PointsMiniBannerContentKey, PointsMiniBannerProps } from './pointsMiniBanner.types';

type PointsMiniBannerMemberProperties = {
  borderLeft?: string;
  gradients?: readonly string[];
  memerLevelBackground?: string;
};

const BASIC_MEMBER_KEYS: PointsMiniBannerContentKey[] = ['R_ZERO', 'R_POSITIVE'];
const DEFAULT_SEARCH_PAGE_PATH = '/search/default.mi';

export const PointsMiniBanner: FC<PointsMiniBannerProps> = ({
  ctaLink,
  pointsBannerDetails,
  icon,
  iconPath,
  openInNewTab,
  testId = 'account-PointsMiniBanner',
}) => {
  const pageContext = useContext(PageContext);
  const memberStatusList = pageContext?.memberStatusList || [];
  const { currentPointBalance, memberData } = useAccountPersistentStore(state => state);
  const { userProfileSummary } = useUserDetailsGlobalStore(state => state);
  const { setOpenBenefitsModalWithUserTier, setTriggerMemberBenefitsModalWithUserTier, setCurrentUserTier } = useStore(
    state => state
  );

  const effectivePointsBalance = useMemo(() => {
    if (currentPointBalance !== undefined) {
      return currentPointBalance;
    }

    const rewardsPoints = Number(memberData?.customer?.loyaltyInformation?.rewards?.currentPointBalance);
    if (!Number.isNaN(rewardsPoints)) {
      return rewardsPoints;
    }

    const summaryPoints = Number(userProfileSummary?.currentPoints);
    return Number.isNaN(summaryPoints) ? 0 : summaryPoints;
  }, [currentPointBalance, memberData, userProfileSummary]);

  const resolvedMemberCode = useMemo(() => {
    const membershipCode = userProfileSummary?.membershipCode;

    if (membershipCode) {
      return MEMBERSHIP_LIFETIME_CODES_MAPPING[membershipCode] || getMemberLevelCode(membershipCode) || membershipCode;
    }

    const rewards = memberData?.customer?.loyaltyInformation?.rewards;
    const tierCode =
      rewards?.eliteLifetimeLevel?.code || getMemberLevelV2(rewards?.level?.code || '', rewards?.levelType?.code || '');

    return tierCode ? MEMBERSHIP_LIFETIME_CODES_MAPPING[tierCode] || getMemberLevelCode(tierCode) || tierCode : 'R';
  }, [memberData, userProfileSummary?.membershipCode]);

  const memberCodeForBanner = resolvedMemberCode || 'R';

  const contentKey = useMemo<PointsMiniBannerContentKey>(() => {
    if (memberCodeForBanner === 'R') {
      return effectivePointsBalance > 0 ? 'R_POSITIVE' : 'R_ZERO';
    }

    switch (memberCodeForBanner) {
      case 'S':
      case 'G':
      case 'P':
      case 'PPC':
      case 'PFS':
        return memberCodeForBanner;
      default:
        return effectivePointsBalance > 0 ? 'R_POSITIVE' : 'R_ZERO';
    }
  }, [effectivePointsBalance, memberCodeForBanner]);

  const content = useMemo(
    () => pointsBannerDetails.find(detail => detail.key === contentKey),
    [pointsBannerDetails, contentKey]
  );

  const memberProperties = useMemo(
    () => getMemberLevelPropertiesByCode(memberCodeForBanner, memberStatusList) as PointsMiniBannerMemberProperties,
    [memberCodeForBanner, memberStatusList]
  );

  const isEliteMember = !BASIC_MEMBER_KEYS.includes(contentKey);
  const accentColor = memberProperties?.borderLeft;
  const iconBackgroundColor = memberProperties?.memerLevelBackground;
  const gradientStart = memberProperties?.gradients?.[0];
  const gradientEnd = memberProperties?.gradients?.[2] || memberProperties?.gradients?.[0];
  const contentTitle = content?.title || '';
  const contentDescription = content?.description || '';
  const contentIcon = content?.icon;
  const contentIconPath = content?.iconPath;
  const contentCtaLink = content?.ctaLink;
  const href = !isEliteMember ? contentCtaLink?.trim() || ctaLink?.trim() || DEFAULT_SEARCH_PAGE_PATH : undefined;

  const bannerTracking = useMemo(() => {
    const trackingProperties = mapTrackingProperties(content?.tracking);
    return {
      trackingProperties,
      customClickTrackValue: getCustomClickTrackValues(trackingProperties, 'internal'),
    };
  }, [content?.tracking]);

  const bannerAriaLabel = `${contentTitle}. ${contentDescription}`;
  const iconClass = contentIcon?.trim() || icon?.trim() || (isEliteMember ? 'icon-star' : 'icon-bed');
  const chevronIconClass = contentIconPath?.trim() || iconPath?.trim() || 'icon-arrow-right';

  const bannerContent = (
    <>
      <span aria-hidden="true" className="points-mini-banner__tier-strip" />
      <span className="points-mini-banner__content">
        <span className="points-mini-banner__icon">
          <Icon iconClass={`${iconClass} points-mini-banner__icon-glyph`} />
        </span>
        <span className="points-mini-banner__text">
          <span className="points-mini-banner__title t-subtitle-s">{contentTitle}</span>
          <span className="points-mini-banner__description t-font-alt-xs">{contentDescription}</span>
        </span>
      </span>
      <span aria-hidden="true" className="points-mini-banner__chevron">
        <Icon iconClass={`${chevronIconClass} points-mini-banner__chevron-icon`} />
      </span>
    </>
  );

  const handleClick = useCallback(() => {
    if (isEliteMember) {
      setCurrentUserTier(memberCodeForBanner);
      setOpenBenefitsModalWithUserTier(true);
      setTriggerMemberBenefitsModalWithUserTier(true);
    }
  }, [
    isEliteMember,
    memberCodeForBanner,
    setCurrentUserTier,
    setOpenBenefitsModalWithUserTier,
    setTriggerMemberBenefitsModalWithUserTier,
  ]);

  if (!pointsBannerDetails?.length || !content) {
    return null;
  }

  return (
    <StyledPointsMiniBanner
      $accentColor={accentColor}
      $iconBackgroundColor={iconBackgroundColor}
      $gradientStart={gradientStart}
      $gradientEnd={gradientEnd}
      data-testid={testId}
      data-component-name="m-account-PointsMiniBanner"
    >
      {isEliteMember ? (
        <button
          type="button"
          className={clsx('points-mini-banner', bannerTracking.customClickTrackValue && 'custom_click_track')}
          aria-label={bannerAriaLabel}
          onClick={handleClick}
          data-custom_click_track_value={bannerTracking.customClickTrackValue}
          data-tracking-content-position={bannerTracking.trackingProperties?.trackingContentPosition}
          data-tracking-description={bannerTracking.trackingProperties?.trackingDescription}
          data-is-click-track={bannerTracking.trackingProperties?.clickTrack}
        >
          {bannerContent}
        </button>
      ) : (
        <a
          className={clsx('points-mini-banner', bannerTracking.customClickTrackValue && 'custom_click_track')}
          aria-label={bannerAriaLabel}
          href={href}
          target={openInNewTab ? '_blank' : undefined}
          rel={openInNewTab ? 'noreferrer' : undefined}
          data-custom_click_track_value={bannerTracking.customClickTrackValue}
          data-tracking-content-position={bannerTracking.trackingProperties?.trackingContentPosition}
          data-tracking-description={bannerTracking.trackingProperties?.trackingDescription}
          data-is-click-track={bannerTracking.trackingProperties?.clickTrack}
        >
          {bannerContent}
        </a>
      )}
    </StyledPointsMiniBanner>
  );
};
